import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { refillKeys } from "./keys";
import { handleApiError, handleResponse } from "~/utils/response";
import { toast } from "sonner";
import { encountersKeys } from "../../pages/encounters/api/queries";
import { quickActionsKeys } from "../../pages/quick-actions/api/queries";



interface ApproveRefillRequestProps {
  refill_id: string;
}

interface Response {
  status: string;
  message: string;
}


/** Variables for rejecting a refill. refill_id is the target refill (sheet refill or specific batch item). */
export interface RejectRefillVariables {
  refill_id: string;
  reason: string;
  details: string;
}

interface DosespotSsoUrlResponse {
  sso_url: string;
}



export const useApproveRefillMutation = (opts: ApproveRefillRequestProps) => {
  const queryClient = useQueryClient();
  return useMutation<Response, unknown, void>({
    mutationKey: refillKeys.approveRefillRequest(opts.refill_id),
    mutationFn: async () => {
      try {
        const response = await apiClient(`/admin/refills/${opts.refill_id}/accept`, {
          method: "PATCH",
        });
        const result = await handleResponse<Response>(response);

        if (result.success) {
          return result.data;
        } else {
          throw new Error(result.error || 'Failed to approve a refill request');
        }
      } catch (error) {
        throw handleApiError(error);
      }
    },
    onSuccess: () => {
      toast.success("Refill request approved successfully!");
      queryClient.invalidateQueries({ queryKey: refillKeys.refill });
      queryClient.invalidateQueries({ queryKey: encountersKeys.all });
      queryClient.invalidateQueries({ queryKey: quickActionsKeys.all });
    }
  })
}


export const useRejectRefillMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<Response, unknown, RejectRefillVariables>({
    mutationKey: [...refillKeys.refill, "reject-refill"],
    mutationFn: async ({ refill_id, reason, details }) => {
      try {
        const response = await apiClient(`/admin/refills/${refill_id}/reject`, {
          method: "PATCH",
          body: JSON.stringify({ reason, details }),
        });
        const result = await handleResponse<Response>(response);

        if (result.success) {
          return result.data;
        } else {
          throw new Error(result.error || 'Failed to reject a refill request');
        }
      } catch (error) {
        throw handleApiError(error);
      }
    },
    onSuccess: () => {
      toast.success("Refill request rejected successfully!");
      queryClient.invalidateQueries({ queryKey: refillKeys.refill });
      queryClient.invalidateQueries({ queryKey: encountersKeys.all });
      queryClient.invalidateQueries({ queryKey: quickActionsKeys.all });
    }
  })
}


export const useDosespotSsoUrlQuery = (
  userDosespotId?: string,
  sessionId?: string,
  encounterId?: string,
  enabled = true
) => {
  return useQuery({
    queryKey: ['dosespot', 'sso-url', userDosespotId, sessionId, encounterId],
    queryFn: async () => {
      if (!userDosespotId || !sessionId || !encounterId) {
        throw new Error("Missing required parameters for Dosespot SSO URL");
      }

      const response = await apiClient(
        `/dosespot/sso-url?patient_dosespot_id=${userDosespotId}&session_id=${sessionId}&encounter_id=${encounterId}`,
        {
          method: 'GET',
        }
      );

      const result = await handleResponse<DosespotSsoUrlResponse>(response);

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch Dosespot SSO URL');
      }
    },
    enabled: enabled && !!userDosespotId && !!sessionId && !!encounterId,
  });
};


// export const useDosespotSsoUrlQuery = (userDosespotId?: string, enabled = true) => {
//   return useQuery({
//     queryKey: ['dosespot', 'sso-url', userDosespotId],
//     queryFn: async () => {
//       const response = await apiClient(`/dosespot/sso-url?patient_dosespot_id=${userDosespotId}`, {
//         method: 'GET',
//       });

//       const result = await handleResponse<DosespotSsoUrlResponse>(response);

//       if (result.success) {
//         return result.data;
//       } else {
//         throw new Error(result.error || 'Failed to fetch Dosespot SSO URL');
//       }
//     },
//     enabled: enabled && !!userDosespotId,
//   });
// };




